// ============================================================================
// Shared application state — single source of truth for cross-module data
// ============================================================================
// All mutable state that was previously ~218 `let` declarations in the
// monolithic main.js lives here as properties of a single `state` object.
// Modules import `state` and read/write properties on it. This avoids
// the temporal-dead-zone problems and circular-dependency issues that
// arise when many modules each declare their own `let` globals.
//
// Convention: read via `state.foo`, write via `state.foo = bar`.
// Do NOT destructure into locals if the value may change — always
// access through `state.` to get the current value.

export const state = {
  // ---- connection ----
  ws: null,
  connected: false,
  intentionalClose: false,
  reconnectTimer: null,
  reconnectAttempts: 0,

  // ---- session ----
  sessionActive: false,
  busy: false,
  selectedDir: "",
  activeCwd: "",
  pendingStart: null,
  pendingDeepLinkSession: null,
  currentSessionId: null,
  expectedSessionId: null,
  resuming: false,
  sessions: [],
  liveSessionIds: new Set(),
  liveCwds: new Set(),
  deletedSessionIds: new Set(),

  // ---- model / config ----
  modelFamilies: [],
  configOptions: [],
  modelConfigId: null,

  // ---- composer ----
  pendingImages: [],
  queuedPromptCount: 0,
  queueState: { running: null, queued: [] },

  // ---- commands / modes ----
  availableCommands: [],
  availableModes: [],
  currentModeId: null,

  // ---- message rendering ----
  agentMsgs: new Map(),
  toolCards: new Map(),
  anonNodeIdCounter: 0,
  loadingHistory: false,
  cacheRestoreToken: 0,
  userPinnedAway: false,
  unreadEvents: 0,

  // ---- node cache (in-memory mirrors) ----
  nodeOrderMap: new Map(),
  sessionMinOrder: 0,
  sessionMaxOrder: -1,

  // ---- paginated history replay ----
  replayingHistory: false,
  replayTotal: 0,
  replayReceived: 0,
  replayPageSize: 50,
  replayOldestCursor: null,
  replayLoaderEl: null,
  replayFirstPageRendered: false,

  // ---- scroll-back ----
  scrollBackFetching: false,
};

// Shared maps and constants that are read-only or conventionally const
export const MAX_RECONNECT_DELAY = 10000;
export const MAX_DOM_MESSAGES = 500;
